import Core from '@/core/Core';
import CargoUnit from "@/domains/CargoUnits/CargoUnit";
import TransportOrder from "@/domains/TransportOrders/TransportOrder";
import AbstractShipmentDocument from "@/domains/Abstract/AbstractShipmentDocument";
import Dictionary from "@/core/types/Dictionary";
import PalletType from "@/domains/CargoUnits/PalletType";

type SizeType = {
  id: string;
  name: string;
};

class CargoUnitService {

  /**
   * Кеш типов паллет. Наполняется из Dictionaries общим эндпоинтом.
   * reactive — чтобы потребители перерисовывались, когда словарь долетит после логина.
   */
  static palletTypesList = reactive(new Dictionary<number, PalletType>());

  /**
   * Порядок сортировки типов паллет для отображения в списке.
   * Единственное место, где задаётся приоритет — новые коды,
   * которых нет в списке, уходят в конец без ошибок.
   */
  private static readonly CODE_SORT_ORDER: string[] = [
    'EUR',
    'HALF EUR',
    'FIN',
    'US',
    'CN',
  ];
  static readonly DEFAULT_PALLET_WEIGHT  = 30;
  static sizeTypeList(): SizeType[] {
    return [
      { id: 'standard', name: 'Паллет' },
      { id: 'individual', name: 'Индивидуальные размеры' },
    ];
  }

  static standardSizeList(): PalletType[] {
    return this.palletTypesList.toArray();
  }

  static getPalletTypeName(palletType: PalletType): string {
    const sizeParts: string[] = [];

    if (!palletType.length.isEmpty()) {
      sizeParts.push(palletType.length.format());
    }
    if (!palletType.width.isEmpty()) {
      sizeParts.push(palletType.width.format());
    }

    return `${palletType.code} ${sizeParts.join('x')} мм`;
  }

  static getPalletTypeListForSelect(): PalletType[] {
    return this.standardSizeList()
      .map((item: PalletType) => {
        item.name = this.getPalletTypeName(item);
        return item;
      })
      .sort((a: PalletType, b: PalletType) =>
        this.getCodeSortIndex(a.code) - this.getCodeSortIndex(b.code),
      );
  }

  private static getCodeSortIndex(code: string): number {
    const index = this.CODE_SORT_ORDER.indexOf(code);
    // неизвестные коды уходят в конец, а не ломают сортировку
    return index === -1 ? this.CODE_SORT_ORDER.length : index;
  }

  /** Импорт паллетомест */
  static importCargoUnits(cargoUnitsOwner: AbstractShipmentDocument | TransportOrder): Promise<boolean> {
    return Core.dialogs.CargoUnitCard.showAsPromise({
      cargoUnitsOwner,
      isImport:  true,
    });
  }

  /**
   * Открыть карточку паллетоместа: null — создание нового, иначе редактирование.
   * Новое или существующее — карточка различает сама по isExisting.
   * Оригинал не мутируется: карточка работает с копией (localCargoUnit).
   */
  static showCard(
    cargoUnit: CargoUnit | null,
    /** Владелец паллетомест — чтобы знать куда добавить/обновить/удалить результат */
    cargoUnitsOwner: AbstractShipmentDocument | TransportOrder,
  ): Promise<boolean> {
    return Core.dialogs.CargoUnitCard.showAsPromise({
      cargoUnit,
      cargoUnitsOwner,
    });
  }
}

export default CargoUnitService;